import React, { createContext, useContext, useState, useCallback, useRef } from "react";

/**
 * In-app confirm / prompt modals — PRYMEBET rule: NEVER use native browser popups
 * (window.confirm / window.alert / window.prompt). Wrap a subtree in <ConfirmProvider>
 * and call const { confirm, promptText } = useConfirm().
 *   await confirm({ title, body, confirmText?, danger? })  -> Promise<boolean>
 *   await promptText({ title, body, placeholder?, defaultValue? }) -> Promise<string|null>
 */
const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null);
  const [value, setValue] = useState("");
  const resolver = useRef(null);

  const settle = useCallback((result) => {
    const r = resolver.current;
    resolver.current = null;
    setOpts(null);
    setValue("");
    if (r) r(result);
  }, []);

  const confirm = useCallback((o = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setValue("");
    setOpts({ mode: "confirm", confirmText: "Confirm", cancelText: "Cancel", ...o });
  }), []);

  const promptText = useCallback((o = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setValue(o.defaultValue || "");
    setOpts({ mode: "prompt", confirmText: "Confirm", cancelText: "Cancel", ...o });
  }), []);

  const cancelResult = opts && opts.mode === "prompt" ? null : false;
  const okResult = opts && opts.mode === "prompt" ? value : true;

  return (
    <ConfirmCtx.Provider value={{ confirm, promptText }}>
      {children}
      {opts && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => settle(cancelResult)} />
          <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#111114] p-5 shadow-2xl">
            {opts.title && <div className="text-base font-semibold text-white mb-1.5">{opts.title}</div>}
            {opts.body && <div className="text-sm leading-relaxed text-white/60 whitespace-pre-line">{opts.body}</div>}
            {opts.mode === "prompt" && (
              <input
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={opts.placeholder || ""}
                onKeyDown={(e) => { if (e.key === "Enter") settle(value); if (e.key === "Escape") settle(null); }}
                className="mt-3 w-full rounded-lg border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm text-white outline-none focus:border-pnr-green/60"
              />
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => settle(cancelResult)}
                className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/80 hover:bg-white/10"
              >
                {opts.cancelText}
              </button>
              <button
                autoFocus={opts.mode !== "prompt"}
                onClick={() => settle(okResult)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold ${
                  opts.danger ? "bg-red-500/90 text-white hover:bg-red-500" : "bg-pnr-green text-black hover:bg-[#00C22D]"
                }`}
              >
                {opts.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmCtx) || {
    // Defensive no-op fallback if used outside a provider (admin tree is always wrapped).
    confirm: async () => false,
    promptText: async () => null,
  };
}
